import { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import type React from 'react';

import type { AutorNaturezaItem, RelatorioCivelFormData } from '../relatorio-civel-page.models';

type UseAutorNaturezaParams = {
  formData: RelatorioCivelFormData;
  setFormData: React.Dispatch<React.SetStateAction<RelatorioCivelFormData>>;
  isViewMode: boolean;
};

export function useAutorNatureza({ formData, setFormData, isViewMode }: UseAutorNaturezaParams) {
  const [novoAutor, setNovoAutor] = useState('');
  const [novaNatureza, setNovaNatureza] = useState('');

  const [editandoAutorNatureza, setEditandoAutorNatureza] = useState<string | null>(null);
  const [valorEdicaoAutor, setValorEdicaoAutor] = useState('');
  const [valorEdicaoNatureza, setValorEdicaoNatureza] = useState('');

  const [confirmandoExclusaoAutor, setConfirmandoExclusaoAutor] = useState<string | null>(null);
  const excluirAutorButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const [quantidadeLinhasAutor, setQuantidadeLinhasAutor] = useState(1);

  const adicionarAutorNatureza = () => {
    if (isViewMode) return;

    if (!novoAutor.trim()) {
      toast.warning('Informe o nome do autor.');
      return;
    }
    if (!novaNatureza) {
      toast.warning('Selecione a natureza da ação.');
      return;
    }

    const novoItem: AutorNaturezaItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      autor: novoAutor.trim(),
      naturezaAcao: novaNatureza,
      dataAdicao: new Date(),
    };

    setFormData((prev) => ({
      ...prev,
      autorNatureza: {
        itens: [...prev.autorNatureza.itens, novoItem],
      },
    }));

    setNovoAutor('');
    setNovaNatureza('');
    toast.success('Autor adicionado com sucesso!');
  };

  const removerAutorNatureza = (id: string) => {
    if (isViewMode) return;

    setFormData((prev) => ({
      ...prev,
      autorNatureza: {
        itens: prev.autorNatureza.itens.filter((item) => item.id !== id),
      },
    }));
    toast.info('Autor removido.');
  };

  const iniciarEdicaoAutorNatureza = (item: AutorNaturezaItem) => {
    if (isViewMode) return;
    setEditandoAutorNatureza(item.id);
    setValorEdicaoAutor(item.autor);
    setValorEdicaoNatureza(item.naturezaAcao);
  };

  const cancelarEdicaoAutorNatureza = () => {
    setEditandoAutorNatureza(null);
    setValorEdicaoAutor('');
    setValorEdicaoNatureza('');
  };

  const salvarEdicaoAutorNatureza = (id: string) => {
    if (isViewMode) return;

    if (!valorEdicaoAutor.trim()) {
      toast.warning('O nome do autor não pode estar vazio.');
      return;
    }
    if (!valorEdicaoNatureza) {
      toast.warning('Selecione a natureza da ação.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      autorNatureza: {
        itens: prev.autorNatureza.itens.map((item) =>
          item.id === id ? { ...item, autor: valorEdicaoAutor.trim(), naturezaAcao: valorEdicaoNatureza } : item,
        ),
      },
    }));

    setEditandoAutorNatureza(null);
    setValorEdicaoAutor('');
    setValorEdicaoNatureza('');
    toast.success('Autor atualizado com sucesso!');
  };

  const adicionarMultiplasLinhasAutor = () => {
    if (isViewMode) return;

    if (quantidadeLinhasAutor < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AutorNaturezaItem[] = [];
    for (let i = 0; i < quantidadeLinhasAutor; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        autor: '',
        naturezaAcao: '',
        dataAdicao: new Date(),
      });
    }

    setFormData((prev) => ({
      ...prev,
      autorNatureza: {
        itens: [...prev.autorNatureza.itens, ...novosItens],
      },
    }));

    toast.success(`${quantidadeLinhasAutor} linha(s) adicionada(s) com sucesso!`);
  };

  const confirmarExclusaoAutor = (id: string) => {
    removerAutorNatureza(id);
    setConfirmandoExclusaoAutor(null);
  };

  const totalAutores = useMemo(() => formData.autorNatureza.itens.length, [formData.autorNatureza.itens.length]);

  return {
    // estado base
    novoAutor,
    setNovoAutor,
    novaNatureza,
    setNovaNatureza,
    quantidadeLinhasAutor,
    setQuantidadeLinhasAutor,
    editandoAutorNatureza,
    confirmandoExclusaoAutor,
    excluirAutorButtonRefs,
    setConfirmandoExclusaoAutor,
    valorEdicaoAutor,
    setValorEdicaoAutor,
    valorEdicaoNatureza,
    setValorEdicaoNatureza,
    totalAutores,

    // handlers
    adicionarAutorNatureza,
    iniciarEdicaoAutorNatureza,
    salvarEdicaoAutorNatureza,
    cancelarEdicaoAutorNatureza,
    adicionarMultiplasLinhasAutor,
    confirmarExclusaoAutor,
  };
}

